import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FiSearch, FiUser, FiX, FiFileText, FiCheck } from "react-icons/fi";
import { motion } from "framer-motion";
import { UploadedDoc } from "./QuoteStepper";
import UploadDocumentZone from "./UploadDocumentZone";
import ClientProfileFileSelector from "./ClientProfileFileSelector";
import "../../styles/QuoteProposal.css";

/* ── Inline SVG icons ── */
const svgBase = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
const FiBriefcase: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);
const FiInfo: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);
const FiPlus: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

/* ── Prop types ── */
export interface DriveData {
  folderId: string;
  fileIds: string[];
  fileNames: string[];
  folderIds: string[];
}

export interface Step1Props {
  search: string;
  clientName: string;
  clientId: string;
  clientEmail: string;
  proposalTitle: string;
  searchClientList: any[];
  isDocumentSelected: boolean;
  isDriveSelected: boolean;
  isQuoteSelected: boolean;
  documents: UploadedDoc[];
  fileURL: string;
  fileNamesForDataTable: string[];
  selectedDriveData: DriveData;
  nowCertQuoteData: any[];
  nowCertPolicyData: any[];
  selectedQuoteId: string;
  selectedQuote: any;
  selectedPolicyId: string;
  selectedPolicy: any;
  selectedPoliciesList?: any[];
  restoredAdditionalDocs?: { fileName: string; fileURL: string; fileId: string }[];
  additionalSlotFiles?: { slotIndex: number; file: File | null; fileName: string }[];
  additionalSlotDrives?: { slotIndex: number; driveData: DriveData }[];
  nowcertsPolicyLoading: boolean;
  clientSearchLoading?: boolean;
  uploadingDocIndex: number | null;
  progress: number;
  validationErrors: Record<string, boolean>;
  onSearchChange: (v: string) => void;
  onClientSelect: (c: any) => void;
  onDocSourceChange: (s: "upload" | "clientProfile" | "ams") => void;
  onDocumentFileDrop: (file: File, index: number) => void;
  onDocumentRemove: (index: number) => void;
  onAddDocSlot: () => void;
  onDriveFileSelect: (d: DriveData) => void;
  onQuoteSelected: (q: any, slotIndex?: number) => void;
  onProposalTitleChange: (v: string) => void;
  selectedQuotesList?: any[];
  onAddQuote?: () => void;
  onRemoveQuote?: (slotIndex: number) => void;
  onAdditionalSlotFile?: (slotIndex: number, file: File | null, fileName: string) => void;
  onAdditionalSlotDrive?: (slotIndex: number, driveData: DriveData | null) => void;
  onSlotClear?: (slotIndex: number) => void;
  onInvalidPdfFile?: () => void;
  onViewDocumentClicked?: (fileName: string) => void;
}

const DOC_TABS = [
  { key: "upload" as const, label: "Upload a Quote Letter" },
  { key: "clientProfile" as const, label: "Select Document from Client Profile" },
  { key: "ams" as const, label: "Select a Quote from AMS" },
];

/* ── Additional Quote Slot Panel ── */
interface SlotPanelProps {
  slotNumber: number;
  slotIndex: number;
  clientId: string;
  initialTab: "upload" | "clientProfile" | "ams";
  nowCertQuoteData: any[];
  nowCertPolicyData: any[];
  nowcertsPolicyLoading: boolean;
  selectedQuote: any;
  selectedPolicy: any;
  initialFileName?: string;
  initialFileURL?: string;
  initialDriveData?: DriveData;
  onQuoteSelected: (q: any, slotIndex?: number) => void;
  onRemove: () => void;
  onFileChange?: (slotIndex: number, file: File | null, fileName: string) => void;
  onDriveChange?: (slotIndex: number, driveData: DriveData | null) => void;
  onSlotClear?: (slotIndex: number) => void;
  onInvalidPdfFile?: () => void;
  onViewDocumentClicked?: (fileName: string) => void;
}

const AdditionalQuoteSlotPanel: React.FC<SlotPanelProps> = ({
  slotNumber,
  slotIndex,
  clientId,
  initialTab,
  nowCertQuoteData,
  nowCertPolicyData,
  nowcertsPolicyLoading,
  selectedQuote,
  selectedPolicy,
  initialFileName,
  initialFileURL,
  initialDriveData,
  onQuoteSelected,
  onRemove,
  onFileChange,
  onDriveChange,
  onSlotClear,
  onInvalidPdfFile,
  onViewDocumentClicked,
}) => {
  const [activeTab, setActiveTab] = useState<"upload" | "clientProfile" | "ams">(initialDriveData ? "clientProfile" : initialTab);
  const [quotesOpen, setQuotesOpen] = useState(true);
  const [policiesOpen, setPoliciesOpen] = useState(true);
  const [sidePoliciesOpen, setSidePoliciesOpen] = useState(true);
  const [slotFile, setSlotFile] = useState<File | null>(null);
  const [hideInitialFile, setHideInitialFile] = useState(false);
  const selectedQuoteId = selectedQuote?.databaseId || "";
  const selectedPolicyId = selectedPolicy?.databaseId || "";

  useEffect(() => {
    setHideInitialFile(false);
  }, [initialFileName, initialFileURL]);

  useEffect(() => {
    if (initialDriveData && initialDriveData.fileIds.length > 0) {
      onDriveChange?.(slotIndex, initialDriveData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chevron = (open: boolean) => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );

  /* Policies accordion reused in Upload + Client Profile tabs */
  const slotPoliciesGrid = clientId ? (
    <div className="qp-accordion" style={{ marginTop: "var(--space-5)" }}>
      <button type="button" className="qp-accordion__header" onClick={() => setSidePoliciesOpen((o) => !o)}>
        <span>Policies from AMS</span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {nowCertPolicyData.length > 0 && <span className="qp-accordion-badge">{nowCertPolicyData.length}</span>}
          {chevron(sidePoliciesOpen)}
        </span>
      </button>
      {sidePoliciesOpen && (
        <div className="qp-accordion__body">
          {nowcertsPolicyLoading ? (
            <div className="qp-loading-state">
              <div className="qp-spinner" />
              <span>Loading policies…</span>
            </div>
          ) : nowCertPolicyData.length === 0 ? (
            <div className="qp-empty-state" style={{ padding: "10px 0" }}>
              <FiInfo size={18} />
              <p style={{ fontSize: 13 }}>No policies found for this client</p>
            </div>
          ) : (
            <div className="qp-quotes-grid">
              {nowCertPolicyData.map((policy: any) => (
                <div
                  key={policy.databaseId}
                  className={`qp-quote-card${selectedPolicyId === policy.databaseId ? " qp-quote-card--selected" : ""}`}
                  onClick={() => onQuoteSelected(policy, slotIndex)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onQuoteSelected(policy, slotIndex);
                  }}
                >
                  {selectedPolicyId === policy.databaseId && (
                    <span className="qp-quote-card__check">
                      <FiCheck size={12} />
                    </span>
                  )}
                  <p className="qp-quote-card__lob">{policy.linesOfBusiness?.[0] || policy.lineOfBusiness || "Insurance"}</p>
                  <p className="qp-quote-card__num">{policy.policyNumber}</p>
                  {policy.totalPremium != null && <p className="qp-quote-card__premium">${Number(policy.totalPremium).toLocaleString()}/yr</p>}
                  {policy.effectiveDate && <p className="qp-quote-card__date">Eff: {policy.effectiveDate}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="qp-step-shell" style={{ marginTop: 16 }}>
      <div className="qp-inner-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 className="qp-section__title" style={{ marginBottom: 0 }}>
            Quote {slotNumber}
          </h3>
          <button
            type="button"
            className="qp-actions__cancel glass-btn"
            style={{ fontSize: 12, padding: "5px 12px", display: "flex", alignItems: "center", gap: 5, color: "#f87171" }}
            onClick={onRemove}
          >
            <i className="fa fa-times" style={{ fontSize: 11 }} />
            Remove
          </button>
        </div>

        {/* Tabs */}
        <div className="qp-doc-tabs" style={{ marginBottom: "var(--space-4)" }}>
          {DOC_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`qp-doc-tabs__tab${activeTab === tab.key ? " qp-doc-tabs__tab--active" : ""}`}
              onClick={() => {
                if (tab.key === activeTab) return;
                onSlotClear?.(slotIndex);
                setSlotFile(null);
                setHideInitialFile(false);
                setActiveTab(tab.key);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Upload tab */}
        {activeTab === "upload" && (
          <div style={{ marginTop: "var(--space-4)" }}>
            {!slotFile && !hideInitialFile && initialFileName ? (
              <div className="qp-files__item">
                <span className="qp-files__name">{initialFileName}</span>
                {initialFileName && (
                  <button
                    type="button"
                    className="qp-files__view"
                    onClick={() => {
                      if (onViewDocumentClicked) {
                        onViewDocumentClicked(initialFileName);
                      } else if (initialFileURL) {
                        window.open(initialFileURL, "_blank", "noopener,noreferrer");
                      }
                    }}
                    title="View"
                  >
                    View
                  </button>
                )}
                <button
                  type="button"
                  className="qp-files__remove"
                  onClick={() => {
                    setHideInitialFile(true);
                    onFileChange?.(slotIndex, null, "");
                  }}
                  title="Remove file"
                >
                  <FiX size={13} />
                </button>
              </div>
            ) : (
              <UploadDocumentZone
                onFileDrop={(file) => {
                  setSlotFile(file);
                  onFileChange?.(slotIndex, file, file.name);
                }}
                onInvalidFile={() => onInvalidPdfFile?.()}
                fileName={slotFile?.name}
              />
            )}
            {slotPoliciesGrid}
          </div>
        )}

        {/* Client Profile tab */}
        {activeTab === "clientProfile" && (
          <div style={{ marginTop: "var(--space-4)" }}>
            <ClientProfileFileSelector
              clientId={clientId}
              onFileIdsSelect={(d) => onDriveChange?.(slotIndex, d)}
              value={
                initialDriveData && initialDriveData.fileIds.length > 0
                  ? { fileIds: initialDriveData.fileIds, fileNames: initialDriveData.fileNames }
                  : undefined
              }
            />
            {slotPoliciesGrid}
          </div>
        )}

        {/* AMS tab */}
        {activeTab === "ams" && (
          <div style={{ marginTop: "var(--space-4)" }}>
            {nowcertsPolicyLoading ? (
              <div className="qp-loading-state">
                <div className="qp-spinner" />
                <span>Loading quotes &amp; policies…</span>
              </div>
            ) : !clientId ? (
              <div className="qp-empty-state">
                <FiInfo size={24} />
                <p>Please select a client first to view their quotes and policies</p>
              </div>
            ) : nowCertQuoteData.length === 0 && nowCertPolicyData.length === 0 ? (
              <div className="qp-empty-state">
                <FiInfo size={24} />
                <p>No quotes or policies found for this client</p>
              </div>
            ) : (
              <>
                {nowCertQuoteData.length > 0 && (
                  <div className="qp-accordion" style={{ marginBottom: 10 }}>
                    <button type="button" className="qp-accordion__header" onClick={() => setQuotesOpen((o) => !o)}>
                      <span>Quotes</span>
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span className="qp-accordion-badge">{nowCertQuoteData.length}</span>
                        {chevron(quotesOpen)}
                      </span>
                    </button>
                    {quotesOpen && (
                      <div className="qp-accordion__body">
                        <div className="qp-quotes-grid">
                          {nowCertQuoteData.map((quote: any) => (
                            <div
                              key={quote.databaseId}
                              className={`qp-quote-card${selectedQuoteId === quote.databaseId ? " qp-quote-card--selected" : ""}`}
                              onClick={() => onQuoteSelected(quote, slotIndex)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") onQuoteSelected(quote, slotIndex);
                              }}
                            >
                              {selectedQuoteId === quote.databaseId && (
                                <span className="qp-quote-card__check">
                                  <FiCheck size={12} />
                                </span>
                              )}
                              <p className="qp-quote-card__lob">{quote.linesOfBusiness?.[0] || quote.lineOfBusiness || "Insurance"}</p>
                              <p className="qp-quote-card__num">{quote.policyNumber}</p>
                              {quote.totalPremium != null && (
                                <p className="qp-quote-card__premium">${Number(quote.totalPremium).toLocaleString()}/yr</p>
                              )}
                              {quote.effectiveDate && <p className="qp-quote-card__date">Eff: {quote.effectiveDate}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {nowCertPolicyData.length > 0 && (
                  <div className="qp-accordion">
                    <button type="button" className="qp-accordion__header" onClick={() => setPoliciesOpen((o) => !o)}>
                      <span>Policies</span>
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span className="qp-accordion-badge">{nowCertPolicyData.length}</span>
                        {chevron(policiesOpen)}
                      </span>
                    </button>
                    {policiesOpen && (
                      <div className="qp-accordion__body">
                        <div className="qp-quotes-grid">
                          {nowCertPolicyData.map((policy: any) => (
                            <div
                              key={policy.databaseId}
                              className={`qp-quote-card${selectedPolicyId === policy.databaseId ? " qp-quote-card--selected" : ""}`}
                              onClick={() => onQuoteSelected(policy, slotIndex)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") onQuoteSelected(policy, slotIndex);
                              }}
                            >
                              {selectedPolicyId === policy.databaseId && (
                                <span className="qp-quote-card__check">
                                  <FiCheck size={12} />
                                </span>
                              )}
                              <p className="qp-quote-card__lob">{policy.linesOfBusiness?.[0] || policy.lineOfBusiness || "Insurance"}</p>
                              <p className="qp-quote-card__num">{policy.policyNumber}</p>
                              {policy.totalPremium != null && (
                                <p className="qp-quote-card__premium">${Number(policy.totalPremium).toLocaleString()}/yr</p>
                              )}
                              {policy.effectiveDate && <p className="qp-quote-card__date">Eff: {policy.effectiveDate}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Step1_ClientQuote: React.FC<Step1Props> = ({
  search,
  clientName,
  clientId,
  proposalTitle,
  searchClientList,
  isDriveSelected,
  isQuoteSelected,
  selectedDriveData,
  documents,
  fileNamesForDataTable,
  nowCertQuoteData,
  nowCertPolicyData,
  selectedQuoteId,
  selectedPolicyId,
  selectedPoliciesList = [],
  restoredAdditionalDocs = [],
  additionalSlotFiles = [],
  additionalSlotDrives = [],
  nowcertsPolicyLoading,
  clientSearchLoading,
  uploadingDocIndex,
  progress,
  validationErrors,
  onSearchChange,
  onClientSelect,
  onDocSourceChange,
  onDocumentFileDrop,
  onDocumentRemove,
  onAddDocSlot,
  onDriveFileSelect,
  onQuoteSelected,
  onProposalTitleChange,
  selectedQuotesList = [],
  onAddQuote,
  onRemoveQuote,
  onAdditionalSlotFile,
  onAdditionalSlotDrive,
  onSlotClear,
  onInvalidPdfFile,
  onViewDocumentClicked,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const [viewUrl, setViewUrl] = useState("");
  const [viewOpen, setViewOpen] = useState(false);
  const [quotesOpen, setQuotesOpen] = useState(true);
  const [policiesOpen, setPoliciesOpen] = useState(true);
  const [extraSlots, setExtraSlots] = useState<number[]>([]);
  const slotCounter = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /* Measure input position for fixed-position dropdown */
  const measureInput = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }
  };

  /* Show dropdown when results arrive */
  useEffect(() => {
    if (searchClientList.length > 0) {
      measureInput();
      setShowDropdown(true);
    } else setShowDropdown(false);
  }, [searchClientList]);

  /* Keep additional quote panels in sync with restored draft selections */
  useEffect(() => {
    const maxDriveSlot = additionalSlotDrives.reduce((m, s) => Math.max(m, s.slotIndex), 0);
    const neededSlots = Math.max(0, selectedQuotesList.length - 1, selectedPoliciesList.length - 1, restoredAdditionalDocs.length, maxDriveSlot);
    setExtraSlots((prev) => {
      if (prev.length === neededSlots) return prev;
      slotCounter.current = neededSlots;
      return Array.from({ length: neededSlots }, (_, i) => i + 1);
    });
  }, [selectedQuotesList.length, selectedPoliciesList.length, restoredAdditionalDocs.length]);

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node) && dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
        setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const activeTab = isDriveSelected ? "clientProfile" : isQuoteSelected ? "ams" : "upload";

  const policiesGrid = (
    <div className="qp-accordion" style={{ marginTop: "var(--space-5)" }}>
      <button type="button" className="qp-accordion__header" onClick={() => setPoliciesOpen((o) => !o)}>
        <span>Policies from AMS</span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {nowCertPolicyData.length > 0 && <span className="qp-accordion-badge">{nowCertPolicyData.length}</span>}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transform: policiesOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      {policiesOpen && (
        <div className="qp-accordion__body">
          {nowcertsPolicyLoading ? (
            <div className="qp-loading-state">
              <div className="qp-spinner" />
              <span>Loading policies…</span>
            </div>
          ) : nowCertPolicyData.length === 0 ? (
            <div className="qp-empty-state" style={{ padding: "10px 0" }}>
              <FiInfo size={18} />
              <p style={{ fontSize: 13 }}>No policies found for this client</p>
            </div>
          ) : (
            <div className="qp-quotes-grid">
              {nowCertPolicyData.map((policy: any) => (
                <div
                  key={policy.databaseId}
                  className={`qp-quote-card${selectedPolicyId === policy.databaseId ? " qp-quote-card--selected" : ""}`}
                  onClick={() => onQuoteSelected(policy)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onQuoteSelected(policy);
                  }}
                >
                  {selectedPolicyId === policy.databaseId && (
                    <span className="qp-quote-card__check">
                      <FiCheck size={12} />
                    </span>
                  )}
                  <p className="qp-quote-card__lob">{policy.linesOfBusiness?.[0] || policy.lineOfBusiness || "Insurance"}</p>
                  <p className="qp-quote-card__num">{policy.policyNumber}</p>
                  {policy.totalPremium != null && <p className="qp-quote-card__premium">${Number(policy.totalPremium).toLocaleString()}/yr</p>}
                  {policy.effectiveDate && <p className="qp-quote-card__date">Eff: {policy.effectiveDate}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="qp-step-shell">
        {/* ── Section 1: Client & Title ── */}
        <div className="qp-inner-card">
          <h3 className="qp-section__title">Client & Quote</h3>
          <p className="qp-section__subtitle">Select a client and provide proposal details</p>

          <div className="qp-form-row">
            {/* Client Search */}
            <div className="qp-field">
              <label className="qp-field__label">
                <FiSearch size={12} /> Select Client *
              </label>
              <div className="qp-search-wrapper" style={{ position: "relative" }}>
                <input
                  ref={inputRef}
                  type="text"
                  className={`qp-field__input${validationErrors.clientName ? " qp-field__input--error" : ""}`}
                  placeholder="Search for a client…"
                  value={search || clientName}
                  onChange={(e) => onSearchChange(e.target.value.slice(0, 255))}
                  onFocus={() => {
                    if (searchClientList.length > 0) {
                      measureInput();
                      setShowDropdown(true);
                    }
                  }}
                  maxLength={255}
                  style={{ paddingRight: clientSearchLoading ? 36 : undefined }}
                />
                {clientSearchLoading && (
                  <div
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div className="qp-spinner qp-spinner--small" />
                  </div>
                )}

                {showDropdown &&
                  searchClientList.length > 0 &&
                  dropdownRect &&
                  createPortal(
                    <motion.div
                      ref={dropdownRef}
                      className="qp-search-dropdown"
                      style={{
                        position: "fixed",
                        top: dropdownRect.top,
                        left: dropdownRect.left,
                        width: dropdownRect.width,
                        zIndex: 99999,
                      }}
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.14 }}
                    >
                      {searchClientList.map((client: any) => (
                        <div
                          key={client.databaseId}
                          className="qp-search-dropdown__item"
                          onClick={() => {
                            onClientSelect(client);
                            setShowDropdown(false);
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              onClientSelect(client);
                              setShowDropdown(false);
                            }
                          }}
                        >
                          <span className="qp-search-dropdown__icon">
                            {client.insuredType === 0 ? <FiUser size={13} /> : <FiBriefcase size={13} />}
                          </span>
                          <span className="qp-search-dropdown__name">{client.name}</span>
                          {client.eMail && <span className="qp-search-dropdown__email">{client.eMail}</span>}
                        </div>
                      ))}
                    </motion.div>,
                    document.body,
                  )}
              </div>
              {validationErrors.clientName && <span className="qp-field__error">Client is required</span>}
            </div>

            {/* Proposal Title */}
            <div className="qp-field">
              <label className="qp-field__label">Proposal Title *</label>
              <input
                type="text"
                className={`qp-field__input${validationErrors.proposalTitle ? " qp-field__input--error" : ""}`}
                placeholder="Enter proposal title"
                value={proposalTitle}
                onChange={(e) => onProposalTitleChange(e.target.value)}
              />
              {validationErrors.proposalTitle && <span className="qp-field__error">Title is required</span>}
            </div>
          </div>
        </div>

        {/* ── Section 2: Document Source ── */}
        <div className="qp-inner-card">
          {/* Source tabs */}
          <div className="qp-doc-tabs">
            {DOC_TABS.map((tab) => (
              <button
                key={tab.key}
                className={`qp-doc-tabs__tab${activeTab === tab.key ? " qp-doc-tabs__tab--active" : ""}`}
                onClick={() => onDocSourceChange(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── UPLOAD TAB ── */}
          {activeTab === "upload" && (
            <div style={{ marginTop: "var(--space-4)" }}>
              <h3 className="qp-section__title">Upload Documents</h3>
              <p className="qp-section__subtitle">Drop PDF files — files will be uploaded when you click Next Step</p>

              {documents.map((doc, idx) => (
                <div key={idx} className="qp-doc-slot">
                  {doc.fileName ? (
                    <div className="qp-files__item">
                      <span className="qp-files__name">{doc.fileName}</span>
                      {doc.fileId && (
                        <span className="qp-files__uploaded" title="Uploaded">
                          <FiCheck size={11} />
                        </span>
                      )}
                      {doc.fileName && (
                        <button
                          type="button"
                          className="qp-files__view"
                          onClick={() => {
                            if (doc.fileId && onViewDocumentClicked) {
                              onViewDocumentClicked(doc.fileName);
                            } else if (doc.file) {
                              const url = URL.createObjectURL(doc.file);
                              window.open(url, '_blank', 'noopener,noreferrer');
                            } else if (doc.fileURL) {
                              window.open(doc.fileURL, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          title="View"
                        >
                          View
                        </button>
                      )}
                      <button type="button" className="qp-files__remove" onClick={() => onDocumentRemove(idx)} title="Remove file">
                        <FiX size={13} />
                      </button>
                    </div>
                  ) : (
                    <UploadDocumentZone
                      onFileDrop={(file) => onDocumentFileDrop(file, idx)}
                      onInvalidFile={() => onInvalidPdfFile?.()}
                      uploading={uploadingDocIndex === idx}
                      progress={uploadingDocIndex === idx ? progress : 0}
                      compact={idx > 0}
                    />
                  )}
                </div>
              ))}

              {validationErrors.documentRequired && (
                <span className="qp-field__error" style={{ display: "block", marginTop: 8 }}>
                  Please upload at least one document
                </span>
              )}

              {clientId && policiesGrid}
            </div>
          )}

          {/* ── CLIENT PROFILE TAB ── */}
          {activeTab === "clientProfile" && (
            <div style={{ marginTop: "var(--space-4)" }}>
              {!clientId ? (
                <div className="qp-empty-state">
                  <FiInfo size={24} />
                  <p>Please select a client first to browse their documents</p>
                </div>
              ) : (
                <>
                  <ClientProfileFileSelector
                    clientId={clientId}
                    onFileIdsSelect={onDriveFileSelect}
                    value={
                      selectedDriveData.fileIds.length > 0
                        ? { fileIds: selectedDriveData.fileIds, fileNames: selectedDriveData.fileNames }
                        : undefined
                    }
                  />
                </>
              )}
              {validationErrors.driveRequired && (
                <span className="qp-field__error" style={{ display: "block", marginTop: 8 }}>
                  Please select a file from client profile
                </span>
              )}

              {clientId && policiesGrid}
            </div>
          )}

          {/* ── AMS TAB ── */}
          {activeTab === "ams" && (
            <div style={{ marginTop: "var(--space-4)" }}>
              {nowcertsPolicyLoading ? (
                <div className="qp-loading-state">
                  <div className="qp-spinner" />
                  <span>Loading quotes &amp; policies…</span>
                </div>
              ) : !clientId ? (
                <div className="qp-empty-state">
                  <FiInfo size={24} />
                  <p>Please select a client first to view their quotes and policies</p>
                </div>
              ) : nowCertQuoteData.length === 0 && nowCertPolicyData.length === 0 ? (
                <div className="qp-empty-state">
                  <FiInfo size={24} />
                  <p>No quotes or policies found for this client</p>
                </div>
              ) : (
                <>
                  {nowCertQuoteData.length > 0 && (
                    <div className="qp-accordion" style={{ marginBottom: 10 }}>
                      <button type="button" className="qp-accordion__header" onClick={() => setQuotesOpen((o) => !o)}>
                        <span>Quotes</span>
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span className="qp-accordion-badge">{nowCertQuoteData.length}</span>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ transform: quotesOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </span>
                      </button>
                      {quotesOpen && (
                        <div className="qp-accordion__body">
                          <div className="qp-quotes-grid">
                            {nowCertQuoteData.map((quote: any) => (
                              <div
                                key={quote.databaseId}
                                className={`qp-quote-card${selectedQuoteId === quote.databaseId ? " qp-quote-card--selected" : ""}`}
                                onClick={() => onQuoteSelected(quote)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") onQuoteSelected(quote);
                                }}
                              >
                                {selectedQuoteId === quote.databaseId && (
                                  <span className="qp-quote-card__check">
                                    <FiCheck size={12} />
                                  </span>
                                )}
                                <p className="qp-quote-card__lob">{quote.linesOfBusiness?.[0] || quote.lineOfBusiness || "Insurance"}</p>
                                <p className="qp-quote-card__num">{quote.policyNumber}</p>
                                {quote.totalPremium != null && (
                                  <p className="qp-quote-card__premium">${Number(quote.totalPremium).toLocaleString()}/yr</p>
                                )}
                                {quote.effectiveDate && <p className="qp-quote-card__date">Eff: {quote.effectiveDate}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {nowCertPolicyData.length > 0 && (
                    <div className="qp-accordion">
                      <button type="button" className="qp-accordion__header" onClick={() => setPoliciesOpen((o) => !o)}>
                        <span>Policies</span>
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span className="qp-accordion-badge">{nowCertPolicyData.length}</span>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ transform: policiesOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </span>
                      </button>
                      {policiesOpen && (
                        <div className="qp-accordion__body">
                          <div className="qp-quotes-grid">
                            {nowCertPolicyData.map((policy: any) => (
                              <div
                                key={policy.databaseId}
                                className={`qp-quote-card${selectedPolicyId === policy.databaseId ? " qp-quote-card--selected" : ""}`}
                                onClick={() => onQuoteSelected(policy)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") onQuoteSelected(policy);
                                }}
                              >
                                {selectedPolicyId === policy.databaseId && (
                                  <span className="qp-quote-card__check">
                                    <FiCheck size={12} />
                                  </span>
                                )}
                                <p className="qp-quote-card__lob">{policy.linesOfBusiness?.[0] || policy.lineOfBusiness || "Insurance"}</p>
                                <p className="qp-quote-card__num">{policy.policyNumber}</p>
                                {policy.totalPremium != null && (
                                  <p className="qp-quote-card__premium">${Number(policy.totalPremium).toLocaleString()}/yr</p>
                                )}
                                {policy.effectiveDate && <p className="qp-quote-card__date">Eff: {policy.effectiveDate}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              {validationErrors.quoteRequired && (
                <span className="qp-field__error" style={{ display: "block", marginTop: 8 }}>
                  Please select a quote or policy
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {/* end qp-step-shell */}

      {/* ── Additional quote slots ── */}
      {extraSlots.map((slotId, i) => (
        <AdditionalQuoteSlotPanel
          key={slotId}
          slotNumber={i + 2}
          slotIndex={i + 1}
          clientId={clientId}
          initialTab={activeTab}
          nowCertQuoteData={nowCertQuoteData}
          nowCertPolicyData={nowCertPolicyData}
          nowcertsPolicyLoading={nowcertsPolicyLoading}
          selectedQuote={selectedQuotesList[i + 1] || null}
          selectedPolicy={selectedPoliciesList[i + 1] || null}
          initialFileName={additionalSlotFiles.find((s) => s.slotIndex === i + 1)?.fileName || restoredAdditionalDocs[i]?.fileName}
          initialFileURL={restoredAdditionalDocs[i]?.fileURL}
          initialDriveData={additionalSlotDrives.find((s) => s.slotIndex === i + 1)?.driveData}
          onQuoteSelected={onQuoteSelected}
          onFileChange={onAdditionalSlotFile}
          onDriveChange={onAdditionalSlotDrive}
          onSlotClear={onSlotClear}
          onInvalidPdfFile={onInvalidPdfFile}
          onViewDocumentClicked={onViewDocumentClicked}
          onRemove={() => {
            setExtraSlots((prev) => prev.filter((s) => s !== slotId));
            onAdditionalSlotFile?.(i + 1, null, "");
            onAdditionalSlotDrive?.(i + 1, null);
            onRemoveQuote?.(i + 1);
          }}
        />
      ))}

      {/* ── Add additional quotes button (common to all tabs) ── */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
        <button
          type="button"
          className="qp-actions__cancel glass-btn"
          style={{ fontSize: 12, padding: "6px 14px", display: "flex", alignItems: "center", gap: 6 }}
          onClick={() => {
            slotCounter.current += 1;
            setExtraSlots((prev) => [...prev, slotCounter.current]);
            onAddQuote?.();
          }}
        >
          <i className="fa fa-plus" style={{ fontSize: 11 }} />
          Add additional quotes
        </button>
      </div>

      {/* ── View file dialog ── */}
      {viewOpen && (
        <div className="qp-dialog-overlay" onClick={() => setViewOpen(false)}>
          <motion.div
            className="qp-dialog qp-dialog--large"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="qp-dialog__header">
              <h3>Document Preview</h3>
              <button className="qp-dialog__close" onClick={() => setViewOpen(false)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="qp-dialog__body" style={{ padding: 0 }}>
              <iframe src={viewUrl} title="Preview" className="qp-dialog__iframe" />
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default React.memo(Step1_ClientQuote);
